/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { api, API_BASE, getToken } from "../lib/apiClient";
import { Download, FileArchive, FileJson, FileSpreadsheet, ChevronDown, ChevronUp } from "lucide-react";
import { fmtDateTimeIST } from "../lib/formatDateTime";
import { useMatch } from "../lib/MatchContext";
import RequireMatch from "../components/RequireMatch";

// Per-section metadata driving the granular download panels.
const SECTIONS = [
  { key: "issues", label: "Raise Issue", desc: "Every issue raised across the venue with photos, comments, seat data.", count_key: "issues", hasCategoryBreakdown: true },
  { key: "pre_match_tasks", label: "Pre-Match Tracker", desc: "Department to-dos with priority, status, due dates.", count_key: "pre_match_tasks" },
  { key: "venue_items", label: "Issues Post Inspection", desc: "Venue readiness inspection items and their status.", count_key: "venue_items" },
  { key: "stocks", label: "Stocks", desc: "Inventory products with inward/outward movements and balances.", count_key: "stocks" },
  { key: "allocations", label: "Allocations", desc: "Godown, rooms, corporate box, parking allocations.", count_key: "allocations" },
  { key: "runsheet", label: "Match Day Run Sheet", desc: "Timed run-of-show entries and their live status.", count_key: "runsheet" },
  { key: "incidents", label: "Incident Logs", desc: "Match-day incidents with severity, status, response notes.", count_key: "incidents" },
  { key: "readiness", label: "Readiness Certificates", desc: "Departmental readiness sign-offs.", count_key: "readiness_certificates" },
  { key: "maps", label: "Critical · Maps", desc: "Stadium map file references.", count_key: "critical_maps" },
  { key: "contacts", label: "Critical · Contacts", desc: "Important contact directory.", count_key: "critical_contacts" },
  { key: "emergency_plans", label: "Critical · Emergency Plans", desc: "Emergency plan document references.", count_key: "critical_emergency_plans" },
];

// Sections whose rows carry `photos` — the XLSX export embeds real
// thumbnail images for these.
const PHOTO_SECTIONS = new Set(["issues", "venue_items", "incidents"]);

function downloadUrl(matchId, section, format, category, extra = {}) {
  const q = new URLSearchParams({ format });
  if (category) q.set("category", category);
  Object.entries(extra).forEach(([k, v]) => { if (v) q.set(k, v); });
  return `${API_BASE}/matches/${matchId}/export/${section}?${q.toString()}`;
}

export default function PostMatchExportPage() {
  const { selectedMatchId } = useMatch();
  const DEMO = selectedMatchId;

  const [summary, setSummary] = useState(null);
  const [issueCategories, setIssueCategories] = useState([]);
  const [issueSubtypeMap, setIssueSubtypeMap] = useState({});
  const [issueCatCounts, setIssueCatCounts] = useState({});
  const [issueSubtypeCounts, setIssueSubtypeCounts] = useState({});
  const [selectedSubtype, setSelectedSubtype] = useState({}); // per-category
  const [err, setErr] = useState("");
  const [downloading, setDownloading] = useState(null);
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    if (!DEMO) return;
    const load = async () => {
      try {
        const [{ data: s }, { data: cats }, { data: issues }] = await Promise.all([
          api.get(`/matches/${DEMO}/export-summary`),
          api.get(`/reference/issue-categories`),
          api.get(`/matches/${DEMO}/issues`),
        ]);
        setSummary(s);
        setIssueCategories(Object.keys(cats.categories || {}));
        setIssueSubtypeMap(cats.categories || {});
        const counts = {};
        issues.forEach((i) => { counts[i.category] = (counts[i.category] || 0) + 1; });
        setIssueCatCounts(counts);
        // Precompute subtype counts per category for the sub-filter labels.
        const scounts = {};
        issues.forEach((i) => {
          if (!scounts[i.category]) scounts[i.category] = {};
          scounts[i.category][i.subtype] = (scounts[i.category][i.subtype] || 0) + 1;
        });
        setIssueSubtypeCounts(scounts);
      } catch (e) {
        setErr(e.response?.data?.detail || "Failed to load summary");
      }
    };
    load();
  }, [DEMO]);

  const doDownload = async (label, url, ext) => {
    setDownloading(label);
    try {
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${getToken()}` },
        credentials: "include",
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const cd = res.headers.get("Content-Disposition") || "";
      const m = /filename=([^;]+)/i.exec(cd);
      const filename = m ? m[1].trim().replace(/^"|"$/g, "") : `matchcommand-${label}.${ext}`;
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(link.href);
    } catch (e) {
      setErr(e.message || "Download failed");
    } finally {
      setDownloading(null);
    }
  };

  const downloadFullZip = () => doDownload("zip", `${API_BASE}/matches/${DEMO}/export.zip`, "zip");
  const downloadFullJson = () => doDownload("json", `${API_BASE}/matches/${DEMO}/export`, "json");

  if (!selectedMatchId) return <RequireMatch />;
  if (err) return <div className="p-8 text-incidentRed" data-testid="export-error">{String(err)}</div>;
  if (!summary) return <div className="p-8 text-zinc-500 font-mono-live">▓▒░ preparing export ░▒▓</div>;

  const totalRecords = Object.values(summary.counts).reduce((a, b) => a + b, 0);

  return (
    <div className="p-6 md:p-10 pb-20" data-testid="page-post-match-export">
      <div className="border-b border-cmdBorder pb-6 mb-6">
        <div className="mc-label">Post-Match · Data Dump</div>
        <h1 className="font-head font-black text-4xl md:text-5xl uppercase tracking-tight leading-none mt-2">Export &amp; Report</h1>
        <p className="text-zinc-400 mt-3 text-sm max-w-2xl">
          Download a full data dump or grab a **separate report** for any single section — Raise Issue (split by category), Pre-Match Tracker, Issues Post Inspection, Stocks, Allocations, Run Sheet, Incidents, Readiness certs and Critical Info.
        </p>
      </div>

      <div className="mc-panel p-5 mb-6" data-testid="export-match-info">
        <div className="mc-label">Match</div>
        <h2 className="font-head font-bold text-2xl uppercase mt-1">{summary.match.name}</h2>
        <div className="text-sm text-zinc-400 mt-1 font-mono-live">
          {summary.match.venue} · {fmtDateTimeIST(summary.match.match_date)} <span className="text-[10px] text-zinc-600">IST</span> · Status: {summary.match.status}
        </div>
      </div>

      {/* Full-match downloads */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
        <button
          onClick={downloadFullJson}
          disabled={downloading === "json"}
          className="mc-panel p-5 text-left mc-panel-hover disabled:opacity-60 group"
          data-testid="export-json-btn"
        >
          <FileJson className="w-7 h-7 mb-2" strokeWidth={1.4} />
          <div className="mc-label">Everything · Nested</div>
          <h3 className="font-head font-bold text-xl uppercase mt-1">Full JSON Dump</h3>
          <p className="text-xs text-zinc-500 mt-2">Match record + every child collection. Great for audits.</p>
          <div className="mt-3 flex items-center gap-2 text-xs uppercase tracking-widest">
            <Download className="w-3 h-3" /> {downloading === "json" ? "Preparing…" : "Download .json"}
          </div>
        </button>
        <button
          onClick={downloadFullZip}
          disabled={downloading === "zip"}
          className="mc-panel p-5 text-left mc-panel-hover disabled:opacity-60 group"
          data-testid="export-zip-btn"
        >
          <FileArchive className="w-7 h-7 mb-2 text-readyGreen" strokeWidth={1.4} />
          <div className="mc-label">Everything · CSV Bundle</div>
          <h3 className="font-head font-bold text-xl uppercase mt-1">Full CSV Zip</h3>
          <p className="text-xs text-zinc-500 mt-2">One CSV per section, plus a summary manifest &amp; JSON dump.</p>
          <div className="mt-3 flex items-center gap-2 text-xs uppercase tracking-widest text-readyGreen">
            <Download className="w-3 h-3" /> {downloading === "zip" ? "Preparing…" : "Download .zip"}
          </div>
        </button>
      </div>

      {/* Per-section downloads */}
      <div className="border-t border-cmdBorder pt-6 mb-3">
        <div className="mc-label">Separate Reports</div>
        <h2 className="font-head font-bold text-2xl uppercase mt-1">By Section</h2>
        <p className="text-xs text-zinc-500 mt-1">Grab a focused CSV or JSON for any single section. Records: <span className="font-mono-live text-white">{totalRecords}</span></p>
      </div>

      <div className="space-y-3">
        {SECTIONS.map((s) => {
          const count = summary.counts[s.count_key] ?? 0;
          const isOpen = expanded[s.key];
          return (
            <div key={s.key} className="mc-panel p-4" data-testid={`export-section-${s.key}`}>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 flex-wrap">
                    <h3 className="font-head font-bold text-lg uppercase tracking-tight">{s.label}</h3>
                    <span className="text-xs font-mono-live text-zinc-500">{count} record{count === 1 ? "" : "s"}</span>
                  </div>
                  <p className="text-xs text-zinc-500 mt-1">{s.desc}</p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <button
                    onClick={() => doDownload(`${s.key}-csv`, downloadUrl(DEMO, s.key, "csv"), "csv")}
                    className="flex items-center gap-1 border border-cmdBorder hover:border-white px-3 py-1.5 text-xs uppercase tracking-widest transition-colors"
                    data-testid={`export-${s.key}-csv`}
                  >
                    <FileSpreadsheet className="w-3 h-3" /> CSV
                  </button>
                  {PHOTO_SECTIONS.has(s.key) && (
                    <button
                      onClick={() => doDownload(`${s.key}-xlsx`, downloadUrl(DEMO, s.key, "xlsx"), "xlsx")}
                      disabled={downloading === `${s.key}-xlsx`}
                      className="flex items-center gap-1 border border-readyGreen text-readyGreen hover:bg-readyGreen hover:text-white px-3 py-1.5 text-xs uppercase tracking-widest transition-colors disabled:opacity-60"
                      data-testid={`export-${s.key}-xlsx`}
                      title="Excel workbook with photos embedded inline"
                    >
                      <FileSpreadsheet className="w-3 h-3" /> {downloading === `${s.key}-xlsx` ? "Preparing…" : "XLSX + Photos"}
                    </button>
                  )}
                  <button
                    onClick={() => doDownload(`${s.key}-json`, downloadUrl(DEMO, s.key, "json"), "json")}
                    className="flex items-center gap-1 border border-cmdBorder hover:border-white px-3 py-1.5 text-xs uppercase tracking-widest transition-colors"
                    data-testid={`export-${s.key}-json`}
                  >
                    <FileJson className="w-3 h-3" /> JSON
                  </button>
                  {s.hasCategoryBreakdown && (
                    <button
                      onClick={() => setExpanded((e) => ({ ...e, [s.key]: !isOpen }))}
                      className="flex items-center gap-1 border border-warnAmber text-warnAmber hover:bg-warnAmber hover:text-white px-3 py-1.5 text-xs uppercase tracking-widest transition-colors"
                      data-testid={`export-${s.key}-by-cat`}
                    >
                      {isOpen ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />} By Category
                    </button>
                  )}
                </div>
              </div>
              {s.hasCategoryBreakdown && isOpen && (
                <div className="mt-4 pt-4 border-t border-cmdBorder" data-testid={`export-${s.key}-categories`}>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                    {issueCategories.map((cat) => {
                      const catCount = issueCatCounts[cat] || 0;
                      const subtypes = issueSubtypeMap[cat] || [];
                      const subCounts = issueSubtypeCounts[cat] || {};
                      const sel = selectedSubtype[cat] || "";
                      // Effective row count for the current filter selection.
                      const effCount = sel ? (subCounts[sel] || 0) : catCount;
                      const extra = sel ? { subtype: sel } : {};
                      const suffix = sel ? `-${sel.replace(/\s+/g, "_")}` : "";
                      return (
                        <div key={cat} className="flex flex-col gap-2 border border-cmdBorder px-3 py-2">
                          <div className="flex items-center justify-between gap-2">
                            <div className="min-w-0">
                              <div className="text-sm font-medium truncate">{cat}</div>
                              <div className="text-[10px] text-zinc-500 font-mono-live">
                                {effCount} record{effCount === 1 ? "" : "s"}{sel ? ` · filtered by ${sel}` : ""}
                              </div>
                            </div>
                            <div className="flex gap-1">
                              <button
                                onClick={() => doDownload(`issues-${cat}${suffix}-csv`, downloadUrl(DEMO, "issues", "csv", cat, extra), "csv")}
                                className="border border-cmdBorder hover:border-white px-2 py-1 text-[10px] uppercase tracking-widest"
                                data-testid={`export-issues-cat-${cat.replace(/\s+/g, "_")}-csv`}
                                disabled={effCount === 0}
                              >
                                CSV
                              </button>
                              <button
                                onClick={() => doDownload(`issues-${cat}${suffix}-xlsx`, downloadUrl(DEMO, "issues", "xlsx", cat, extra), "xlsx")}
                                className="border border-readyGreen text-readyGreen hover:bg-readyGreen hover:text-white px-2 py-1 text-[10px] uppercase tracking-widest"
                                data-testid={`export-issues-cat-${cat.replace(/\s+/g, "_")}-xlsx`}
                                disabled={effCount === 0}
                                title="Excel workbook with photos embedded inline"
                              >
                                XLSX+📷
                              </button>
                              <button
                                onClick={() => doDownload(`issues-${cat}${suffix}-json`, downloadUrl(DEMO, "issues", "json", cat, extra), "json")}
                                className="border border-cmdBorder hover:border-white px-2 py-1 text-[10px] uppercase tracking-widest"
                                data-testid={`export-issues-cat-${cat.replace(/\s+/g, "_")}-json`}
                                disabled={effCount === 0}
                              >
                                JSON
                              </button>
                            </div>
                          </div>
                          {subtypes.length > 0 && (
                            <div className="flex items-center gap-2 flex-wrap pt-1 border-t border-cmdBorder">
                              <span className="mc-label text-[10px]">Filter</span>
                              <select
                                value={sel}
                                onChange={(e) => setSelectedSubtype((m) => ({ ...m, [cat]: e.target.value }))}
                                className={`bg-black border text-[11px] py-1 px-2 ${sel ? "border-readyGreen text-readyGreen" : "border-cmdBorder text-zinc-300"}`}
                                data-testid={`export-issues-cat-${cat.replace(/\s+/g, "_")}-subtype`}
                              >
                                <option value="">All subtypes ({catCount})</option>
                                {subtypes.map((s) => (
                                  <option key={s} value={s}>
                                    {s} ({subCounts[s] || 0})
                                  </option>
                                ))}
                              </select>
                              {sel && (
                                <button
                                  onClick={() => setSelectedSubtype((m) => ({ ...m, [cat]: "" }))}
                                  className="text-[10px] uppercase tracking-widest text-zinc-400 hover:text-white"
                                  data-testid={`export-issues-cat-${cat.replace(/\s+/g, "_")}-clear`}
                                >
                                  Clear
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-6 mc-panel p-4 border-l-4 border-warnAmber">
        <div className="mc-label">Note</div>
        <p className="text-xs text-zinc-500 mt-2">
          Photo/attachment binaries are referenced by storage path inside every export. Retrieve individual files with
          <span className="font-mono-live text-white"> GET /api/files/{"{path}"}?auth={"{token}"}</span>.
        </p>
      </div>
    </div>
  );
}
